import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createVNode as _createVNode, createTextVNode as _createTextVNode, createElementVNode as _createElementVNode, unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createElementBlock as _createElementBlock, withModifiers as _withModifiers } from "vue"

const _hoisted_1 = { key: 0 }
const _hoisted_2 = { key: 0 }

import { PropType, computed, onBeforeUnmount, onMounted, ref } from 'vue';
import ActionContentForm from '@/components/action/ActionContentForm.vue';
import {
  TYPE_ACTION,
  TYPE_INFOGRAPHIC,
  NEW_ID,
  TYPE_INITIATIVE,
  TYPE_QUESTION,
} from '@/util/constants';
import { InfoItem } from '@/models/info-item.model';
import { Hero } from '@/models/hero.model';
import { Action, Step } from '@/models/action.model';
import { QuizQuestion } from '@/models/quiz-question.model';
import CurrentLanguage from '@/components/CurrentLanguage.vue';
import { useEvents, Event } from '@/services/events.composable';
import { Actions, Mutations, ShiftCMSState } from '@/store';
import { useStore } from 'vuex';
import { ContentStoreState, ContentStoreActions } from '@/stores/content.store';
import { DataResponse } from '@/services/base.service';
import { InfoGraphic } from '@/models/info-graphic.model';
import InfoGraphicContentForm from '@/components/infographic/InfoGraphicContentForm.vue';
import InitiativeContentForm from '@/components/initiative/InitiativeContentForm.vue';
import { Initiative } from '@/models/initiative.model';
import QuestionContentForm from '@/components/quiz/QuestionContentForm.vue';
import QuestionStats from '@/components/quiz/QuestionStats.vue';
import { Contact } from '@/models/contact.model';
import { Category } from '@/models/category.model';
import { Address } from '@/models/info.model';


export default /*@__PURE__*/_defineComponent({
  __name: 'ContentItemForm',
  props: {
  data: {
    type: Object as PropType<{
      type: string;
      contentItem:
        | InfoItem
        | Hero
        | Action
        | QuizQuestion
        | InfoGraphic
        | Initiative;
    }>,
    required: true,
  },
},
  setup(__props) {

const events = useEvents();
const store = useStore<ShiftCMSState & { cns: ContentStoreState }>();

const props = __props;

const model = ref<
  InfoItem | Hero | Action | QuizQuestion | InfoGraphic | Initiative
>({
  ...props.data.contentItem,
});

let currentValue = ref<
  InfoItem | Hero | Action | QuizQuestion | InfoGraphic | Initiative
>({ ...model.value });

const action = computed<string>(() =>
  model.value.id === NEW_ID ? 'Add' : 'Save',
);

function isInfoGraphic(
  d: InfoItem | Hero | Action | QuizQuestion | InfoGraphic | Initiative,
): d is InfoGraphic {
  return props.data.type == TYPE_INFOGRAPHIC;
}

function isAction(
  d: InfoItem | Hero | Action | QuizQuestion | InfoGraphic | Initiative,
): d is Action {
  return props.data.type == TYPE_ACTION;
}

function isInitiative(
  d: InfoItem | Hero | Action | QuizQuestion | InfoGraphic | Initiative,
): d is Initiative {
  return props.data.type == TYPE_INITIATIVE;
}

function isQuestion(
  d: InfoItem | Hero | Action | QuizQuestion | InfoGraphic | Initiative,
): d is QuizQuestion {
  return props.data.type == TYPE_QUESTION;
}

function getSimpleValue(i: any) {
  return isInitiative(i)
    ? {
        title: i.title,
        shortDescription: i.shortDescription,
        description: i.description,
        quizTitle: i.quizTitle,
        website: i.website,
        impactArea: i.impactArea,
        recommended: i.recommended,
        favourite: i.favourite,
        mainCategory: i.mainCategory.id,
        categories: [...(i.categories || [])].map((c: Category) => c.id),
        address: i.address,
        contacts: [...(i.contacts || [])].map((c: Contact) => ({
          firstName: c.firstName,
          lastName: c.lastName,
        })),
      }
    : isAction(i)
    ? {
        title: i.title,
        shortDescription: i.shortDescription,
        description: i.description,
        categories: [...(i.categories || [])].map((c: Category) => c.id),
        steps: [...(i.steps || [])].map((c: Step) => c.id),
      }
    : isQuestion(i)
    ? { title: i.title, answers: i.answers }
    : isInfoGraphic(i)
    ? { title: i.title }
    : {};
}

const isDirty = computed<boolean>(() => {
  const isDirty =
    JSON.stringify(getSimpleValue(model.value)) !==
    JSON.stringify(getSimpleValue(currentValue.value));

  store.commit(Mutations.SetDirtyState, isDirty);
  return isDirty;
});

const valid = ref<boolean>(true);

const cancel = () => {
  store.commit(Mutations.HideDrawer);
};

const save = () => saveItem(null, false);

const saveItem = async (e: unknown, close = true) => {
  let res: DataResponse<InfoItem | Hero | Action | QuizQuestion | null> = {
    status: false,
    data: null,
  };

  if (model.value.id === NEW_ID) {
    switch (props.data.type) {
      case TYPE_INFOGRAPHIC:
        res = await store.dispatch(
          ContentStoreActions.CreateInfoGraphic,
          model.value,
        );
        break;
      case TYPE_ACTION:
        res = await store.dispatch(
          ContentStoreActions.CreateAction,
          model.value,
        );
        break;
      case TYPE_INITIATIVE:
        res = await store.dispatch(
          ContentStoreActions.CreateInitiative,
          model.value,
        );
        break;
    }
  } else {
    switch (props.data.type) {
      case TYPE_INFOGRAPHIC:
        res = await store.dispatch(
          ContentStoreActions.UpdateInfoGraphic,
          model.value,
        );
        break;
      case TYPE_ACTION:
        res = await store.dispatch(
          ContentStoreActions.UpdateAction,
          model.value,
        );
        break;
      case TYPE_INITIATIVE:
        res = await store.dispatch(
          ContentStoreActions.UpdateInitiative,
          model.value,
        );
        break;
      case TYPE_QUESTION:
        res = await store.dispatch(
          ContentStoreActions.UpdateQuizQuestion,
          model.value,
        );
        break;
    }
  }

  if (res.status) {
    store.dispatch(Actions.ShowNotification, {
      message: `Saved ${props.data.type}`,
      type: 'success',
    });
    events.emit(Event.UpdateContentItem);
    if (close) {
      cancel();
    }
  } else {
    store.dispatch(Actions.ShowNotification, {
      message: `Failed to saved ${props.data.type}`,
      type: 'error',
    });
  }

  currentValue.value = { ...model.value };
  store.commit(Mutations.SetDirtyState, false);
};

const toggleInitiativePublished = async (initiative: Initiative) => {
  if (initiative) {
    await store.dispatch(ContentStoreActions.UpdateInitiative, initiative);

    store.dispatch(Actions.ShowNotification, {
      message: !initiative.published
        ? 'Initiative is no longer published'
        : 'Initiative is published',
      type: !initiative.published ? 'info' : 'success',
    });
  }
};

const updateInitiativeStatus = async (initiative: Initiative) => {
  if (initiative) {
    const result = await store.dispatch(
      ContentStoreActions.UpdateInitiative,
      initiative,
    );

    store.dispatch(Actions.ShowNotification, {
      message: `Initiative status is now ${initiative.status}`,
      type: 'info',
    });

    model.value = { ...result.data };
    currentValue.value = { ...model.value };
    store.commit(Mutations.SetDirtyState, false);
  }
};

const toggleActionPublished = async (action: Action) => {
  if (action) {
    await store.dispatch(ContentStoreActions.UpdateAction, action);

    store.dispatch(Actions.ShowNotification, {
      message: !action.published
        ? 'Action is no longer published'
        : 'Action is published',
      type: !action.published ? 'info' : 'success',
    });
  }
};

const removeContact = async (item: Contact) => {
  if (item) {
    await store.dispatch(ContentStoreActions.RemoveContact, item);

    store.dispatch(Actions.ShowNotification, {
      message: 'Contact has been deleted',
      type: 'info',
    });
  }
};

const updateStoryContent = async () => {
  if (model.value.id !== NEW_ID) {
    switch (props.data.type) {
      case TYPE_INFOGRAPHIC:
        Object.assign(
          model.value,
          await store.dispatch(
            ContentStoreActions.GetInfoGraphic,
            props.data.contentItem.id,
          ),
        );
        break;
      case TYPE_ACTION:
        Object.assign(
          model.value,
          await store.dispatch(
            ContentStoreActions.GetAction,
            props.data.contentItem.id,
          ),
        );
        break;
      case TYPE_INITIATIVE:
        Object.assign(
          model.value,
          await store.dispatch(
            ContentStoreActions.GetInitiative,
            props.data.contentItem.id,
          ),
        );
        break;
      case TYPE_QUESTION:
        Object.assign(
          model.value,
          await store.dispatch(
            ContentStoreActions.GetQuizQuestion,
            props.data.contentItem.id,
          ),
        );
        break;
    }
  }
  currentValue.value = { ...model.value };
};

const removeItem = async () => {
  if (
    model.value &&
    model.value.id !== NEW_ID &&
    confirm(
      `Are you absolutely sure you want to delete this "${props.data.type}"?`,
    )
  ) {
    switch (props.data.type) {
      case TYPE_INFOGRAPHIC:
        store.dispatch(ContentStoreActions.DeleteInfoGraphic, model.value.id);
        break;
      case TYPE_ACTION:
        store.dispatch(ContentStoreActions.DeleteAction, model.value.id);
        break;
      case TYPE_INITIATIVE:
        store.dispatch(ContentStoreActions.DeleteInitiative, model.value.id);
        break;
    }

    store.commit(Mutations.HideDrawer);
  }
};

onMounted(() => {
  events.on(Event.LanguageChanged, updateStoryContent);

  updateStoryContent();
});

onBeforeUnmount(() => {
  events.off(Event.LanguageChanged, updateStoryContent);
});

return (_ctx: any,_cache: any) => {
  const _component_v_container = _resolveComponent("v-container")!
  const _component_v_col = _resolveComponent("v-col")!
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_v_row = _resolveComponent("v-row")!
  const _component_v_form = _resolveComponent("v-form")!

  return (_openBlock(), _createBlock(_component_v_form, {
    modelValue: valid.value,
    "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((valid).value = $event)),
    onSubmit: _withModifiers(saveItem, ["prevent"])
  }, {
    default: _withCtx(() => [
      _createVNode(_component_v_container, null, {
        default: _withCtx(() => [
          _createElementVNode("h2", null, [
            _createTextVNode(_toDisplayString(_ctx.$t(action.value)) + " " + _toDisplayString(__props.data.type) + " ", 1),
            _createVNode(CurrentLanguage)
          ]),
          (__props.data.type === _unref(TYPE_INFOGRAPHIC) && isInfoGraphic(model.value))
            ? (_openBlock(), _createBlock(InfoGraphicContentForm, {
                key: 0,
                modelValue: model.value,
                "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((model).value = $event))
              }, null, 8, ["modelValue"]))
            : _createCommentVNode("", true),
          (__props.data.type === _unref(TYPE_ACTION) && isAction(model.value))
            ? (_openBlock(), _createBlock(ActionContentForm, {
                key: 1,
                "onUpdate:published": toggleActionPublished,
                modelValue: model.value,
                "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((model).value = $event))
              }, null, 8, ["modelValue"]))
            : _createCommentVNode("", true),
          (__props.data.type === _unref(TYPE_INITIATIVE) && isInitiative(model.value))
            ? (_openBlock(), _createBlock(InitiativeContentForm, {
                key: 2,
                modelValue: model.value,
                "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((model).value = $event)),
                "onUpdate:published": toggleInitiativePublished,
                "onUpdate:status": updateInitiativeStatus,
                onRemoveContact: removeContact
              }, null, 8, ["modelValue"]))
            : _createCommentVNode("", true),
          (__props.data.type === _unref(TYPE_QUESTION) && isQuestion(model.value))
            ? (_openBlock(), _createBlock(QuestionContentForm, {
                key: 3,
                modelValue: model.value,
                "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((model).value = $event))
              }, null, 8, ["modelValue"]))
            : _createCommentVNode("", true),
          (__props.data.type === _unref(TYPE_QUESTION) && isQuestion(model.value))
            ? (_openBlock(), _createBlock(QuestionStats, {
                key: 4,
                question: model.value
              }, null, 8, ["question"]))
            : _createCommentVNode("", true)
        ]),
        _: 1
      }),
      _createVNode(_component_v_row, null, {
        default: _withCtx(() => [
          _createVNode(_component_v_col, { col: "2" }),
          _createVNode(_component_v_col, null, {
            default: _withCtx(() => [
              (__props.data.type !== _unref(TYPE_QUESTION))
                ? (_openBlock(), _createBlock(_component_v_btn, {
                    key: 0,
                    class: "mt-2",
                    type: "button",
                    color: "warning",
                    onClick: removeItem,
                    block: ""
                  }, {
                    default: _withCtx(() => _cache[5] || (_cache[5] = [
                      _createTextVNode("Delete")
                    ])),
                    _: 1
                  }))
                : _createCommentVNode("", true)
            ]),
            _: 1
          }),
          _createVNode(_component_v_col, null, {
            default: _withCtx(() => [
              _createVNode(_component_v_btn, {
                class: "mt-2",
                type: "button",
                onClick: cancel,
                block: ""
              }, {
                default: _withCtx(() => _cache[6] || (_cache[6] = [
                  _createTextVNode("Cancel")
                ])),
                _: 1
              })
            ]),
            _: 1
          }),
          _createVNode(_component_v_col, null, {
            default: _withCtx(() => [
              _createVNode(_component_v_btn, {
                class: "mt-2",
                type: "button",
                onClick: save,
                color: "success",
                block: ""
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(action.value) + " ", 1),
                  (isDirty.value)
                    ? (_openBlock(), _createElementBlock("span", _hoisted_1, "*"))
                    : _createCommentVNode("", true)
                ]),
                _: 1
              })
            ]),
            _: 1
          }),
          _createVNode(_component_v_col, null, {
            default: _withCtx(() => [
              _createVNode(_component_v_btn, {
                class: "mt-2",
                type: "submit",
                color: "success",
                block: ""
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(action.value) + " and close ", 1),
                  (isDirty.value)
                    ? (_openBlock(), _createElementBlock("span", _hoisted_2, "*"))
                    : _createCommentVNode("", true)
                ]),
                _: 1
              })
            ]),
            _: 1
          }),
          _createVNode(_component_v_col, { col: "2" })
        ]),
        _: 1
      })
    ]),
    _: 1
  }, 8, ["modelValue"]))
}
}

})